import jsonLogic from 'json-logic-js'
import { toRaw } from 'vue'

// conditions
export function isCondition (fieldProp) {
  return jsonLogic.is_logic(fieldProp)
}

/**
 * Condition parser function
 * Builds a JsonLogic data object from fields referenced in the condition
 * @param {Object} condition - An object representing a JSON logic condition
 * @param {Object} guJsonLogicLookup - Object of key/value pair to represent the data for evaluating json logic condition
 * @returns {Boolean}
 */
export function parseCondition (condition, guJsonLogicLookup) {
  if (condition == null || typeof condition === 'undefined') return ''
  if (typeof condition === 'boolean') return condition

  return jsonLogic.apply(condition, guJsonLogicLookup)
}

export function isVisible (config, guJsonLogicLookup = null) {
  if (!Object.hasOwn(config, 'visible')) return true

  const rawConfig = toRaw(config)

  if (!guJsonLogicLookup) return parseCondition(rawConfig.visible)

  return parseCondition(rawConfig.visible, guJsonLogicLookup)
}


/**
 * Component property condition parser function
 * Evaluates top level properties if they are json logic conditions, returns updated config with the json logic conditions evaluated
 * @param {Object} componentConfig - An object representing the configured component (usually from a content override)
 * @param {Object} guJsonLogicLookup - Object of key/value pair to represent the data for evaluating json logic condition
 * @returns {Object} - A new object with evaluated conditions
 */
export function evaluateComponentConditions (componentConfig, guJsonLogicLookup) {
  const result = { ...componentConfig }
  for (const [key, value] of Object.entries(componentConfig)) {
    // a try/catch is added here as object with one key value pair throws a false positive with jsonLogic.is_logic helper
    // i.e. jsonLogic.is_logic({ "scope" : "app-details.actions" }) returns true when it should not as it is not a json logic condition
    try {
      if (isCondition(value)) result[key] = parseCondition(value, guJsonLogicLookup)
    } catch (err) {
      console.error('Failed to evaluate component json logic condition', `key: ${key}`, value)
      console.error(err)
    }
  }
  return result
}

/**
 * Normalize the config object defined in the content override
 * Evaluates json logic if properties in config are json logic condition
 * Add a visible prop to component config
 * @param {Object} componentConfig - An object representing the configured component (usually from a content override)
 * @param {Object} guJsonLogicLookup - Object of key/value pair to represent the data for evaluating json logic condition
 * @returns {Object} - A new normalized config object
 */
export function normalizeComponentConfig (componentConfig, guJsonLogicLookup) {
  if (typeof componentConfig === 'string') componentConfig = { name: componentConfig }
  const result = evaluateComponentConditions(componentConfig, guJsonLogicLookup)
  // if visible prop is not in result, show component by default
  if (('visible' in result) === false) result.visible = true
  return result
}
